import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQ.css';

/*
 * FAQ — copy rewritten for the new positioning.
 * Rule applied throughout: receipts are the mechanism, never the headline.
 * Answers lead with outcomes (Get Paid / Get Protected / Get Answers)
 * and only explain receipts when the question is literally "how does it work."
 *
 * ⚠️ VERIFY BEFORE SHIP (marked inline):
 *  - Privacy answer must match your actual privacy policy word-for-word.
 *  - Pricing answer assumes free tier with no committed paid plan yet.
 *  - Rewards answer stays deliberately light on redemption mechanics —
 *    tighten once redemption is user-facing and stable.
 */

const FAQ = ({ onContactClick }) => {
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqs = [
    {
      question: 'What is KACHNG?',
      answer:
        'KACHNG turns your everyday purchases into money back, protection, and answers. You get a personal @kachng.co email address — use it at checkout, and every receipt sent there is automatically organized and put to work: rewards on what you buy, reminders before return windows and warranties expire, and instant answers about your spending.'
    },
    {
      question: 'How does it work?',
      answer:
        'Give the store your @kachng.co address when you check out, or forward receipts from your own inbox. That\u2019s the whole job. KACHNG reads each receipt down to the item level and handles the rest — no scanning, no photographing, no spreadsheets, no manual entry.'
    },
    {
      question: 'Do I need to link my bank account?',
      answer:
        'No. KACHNG never asks for your bank login and never sees your accounts. It works entirely from your receipts. Your bank knows you spent $84 at Target. KACHNG knows exactly what you bought, what it cost, and when you can still return it. That\u2019s more useful — and far less invasive.'
    },
    {
      question: 'Is my purchase data private?',
      // ⚠️ VERIFY: this must match your actual privacy policy exactly.
      answer:
        'Your purchase data belongs to you. It powers your rewards, your reminders, and your answers — it works for you, not on you. You can delete your data at any time. For the full details, read our Privacy Policy.'
    },
    {
      question: 'What does KACHNG cost?',
      // ⚠️ VERIFY: assumes free tier, no paid plan promised yet.
      answer:
        'KACHNG is free to download and free to use. Set up takes about thirty seconds: download the app, claim your @kachng.co address, and use it on your next purchase.'
    },
    {
      question: 'What kinds of receipts does KACHNG support?',
      answer:
        'Any digital receipt — email receipts from stores, restaurants, and online orders, including PDF and attached receipts. If a merchant can email it, KACHNG can organize it. Next time you\u2019re asked "email or printed receipt?", you finally have a reason to say email.'
    },
    {
      question: 'How do rewards work?',
      answer:
        'KACHNG Rewards is for users aged 18 or older. Rewards earned while under 18 are ineligible and forfeited if identified. Eligible users earn points on the receipts they bring in — the same purchases you were making anyway. No rebate forms, no barcode hunting, no hoops. Your points balance and history live right in the app.'
    },
    {
      question: 'What are return and warranty reminders?',
      answer:
        'Every purchase has a clock on it — a return window, a warranty period — and stores are counting on you to lose track. KACHNG tracks those clocks automatically and warns you before they run out, so a $340 jacket that doesn\u2019t fit becomes $340 back instead of a closet regret.'
    },
    {
      question: 'Can I ask KACHNG questions about my spending?',
      answer:
        'Yes — that\u2019s the point. Ask things like "how much have I spent on coffee this month," "when did I buy that blender," or "what did I pay for this last time." KACHNG answers from your own purchase history, item by item. It\u2019s memory for your money.'
    },
    {
      question: 'Is KACHNG on Android?',
      answer:
        'KACHNG is live on iOS today, and Android is in active development. In the meantime, anyone can claim their @kachng.co address so their purchase history is already built the day the app lands.'
    },
    {
      question: 'Is it true you plant trees?',
      answer:
        'Yes. When you hit milestones in KACHNG, we plant a tree with One Tree Planted. Every paper receipt you skip is waste that never happens — and your milestones put trees in the ground on top of it.'
    }
  ];

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="faq-nav">
        <Link to="/" className="back-link">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"/>
          </svg>
          Back to Home
        </Link>
      </div>

      <div className="faq-container">
        <header className="faq-header">
          <div className="brand-mark">KACHNG</div>
          <h1 className="faq-title">Frequently Asked Questions</h1>
          <p className="faq-intro">
            Straight answers about how KACHNG gets you paid, protected, and
            informed on every purchase.
          </p>
        </header>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${openQuestion === index ? 'active' : ''}`}>
              <button
                className="faq-question"
                onClick={() => toggleQuestion(index)}
                aria-expanded={openQuestion === index}
              >
                <span className="question-text">{faq.question}</span>
                <svg className="chevron" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7,10L12,15L17,10H7Z"/>
                </svg>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="faq-footer">
          <div className="still-questions">
            <h3>Still have questions?</h3>
            <p>Ask the person who built it.</p>
                <button className="contact-button" onClick={onContactClick}>
                  Talk to the founder
                </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;